/* eslint-disable react-refresh/only-export-components */
/**
 * Consent Context
 *
 * React context for managing cookie consent state throughout the application.
 * Provides hooks and components to check and manage user consent.
 *
 * This file intentionally exports the provider component, hooks, and context
 * object for flexibility in usage patterns.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { consentService, type ConsentState, type ConsentCategory } from '@/services/consentService';

// Context value type
interface ConsentContextValue {
  // Current consent state
  consentState: ConsentState;

  // Whether user has made any consent choice
  hasConsented: boolean;

  // Check if specific category is consented
  hasConsentFor: (category: ConsentCategory) => boolean;

  // Consent actions
  acceptAll: () => void;
  rejectAll: () => void;
  setPreferences: (preferences: Partial<Omit<ConsentState, 'essential'>>) => void;
  withdrawConsent: () => void;

  // Show preferences modal
  showPreferences: boolean;
  setShowPreferences: (show: boolean) => void;
}

// Default context value (for SSR safety)
const defaultContextValue: ConsentContextValue = {
  consentState: { essential: true, analytics: false, marketing: false },
  hasConsented: false,
  hasConsentFor: (category) => category === 'essential',
  acceptAll: () => {},
  rejectAll: () => {},
  setPreferences: () => {},
  withdrawConsent: () => {},
  showPreferences: false,
  setShowPreferences: () => {},
};

// Create context
const ConsentContext = createContext<ConsentContextValue>(defaultContextValue);

// Provider component
export function ConsentProvider({ children }: { children: React.ReactNode }) {
  // Local state synced with service
  const [consentState, setConsentState] = useState<ConsentState>(
    consentService.getConsentState()
  );
  const [hasConsented, setHasConsented] = useState<boolean>(
    consentService.hasConsented()
  );
  const [showPreferences, setShowPreferences] = useState<boolean>(false);

  // Subscribe to consent changes
  useEffect(() => {
    const unsubscribe = consentService.subscribe((newState) => {
      setConsentState(newState);
      setHasConsented(consentService.hasConsented());
    });

    return unsubscribe;
  }, []);

  // Consent action handlers
  const acceptAll = useCallback(() => {
    consentService.acceptAll();
  }, []);

  const rejectAll = useCallback(() => {
    consentService.rejectAll();
  }, []);

  const setPreferences = useCallback((preferences: Partial<Omit<ConsentState, 'essential'>>) => {
    consentService.setPreferences(preferences);
    setShowPreferences(false);
  }, []);

  const withdrawConsent = useCallback(() => {
    consentService.withdrawConsent();
  }, []);

  const hasConsentFor = useCallback((category: ConsentCategory): boolean => {
    return consentService.hasConsentFor(category);
  }, []);

  // Memoize context value
  const contextValue = useMemo<ConsentContextValue>(() => ({
    consentState,
    hasConsented,
    hasConsentFor,
    acceptAll,
    rejectAll,
    setPreferences,
    withdrawConsent,
    showPreferences,
    setShowPreferences,
  }), [
    consentState,
    hasConsented,
    hasConsentFor,
    acceptAll,
    rejectAll,
    setPreferences,
    withdrawConsent,
    showPreferences,
  ]);

  return (
    <ConsentContext.Provider value={contextValue}>
      {children}
    </ConsentContext.Provider>
  );
}

// Hook to use consent context
export function useConsent(): ConsentContextValue {
  const context = useContext(ConsentContext);
  if (!context) {
    throw new Error('useConsent must be used within a ConsentProvider');
  }
  return context;
}

// Hook to check specific consent category (convenience)
export function useConsentFor(category: ConsentCategory): boolean {
  const { hasConsentFor, hasConsented } = useConsent();
  if (category === 'essential') return true;
  return hasConsented && hasConsentFor(category);
}

// Export context for advanced use cases
export { ConsentContext };
